import {
  composeRestyleFunctions,
  spacing,
  useRestyle,
  color,
  border,
  layout,
  backgroundColor,
  opacity,
  position,
  shadow,
} from "@shopify/restyle";
import React from "react";

import { Theme } from "../../../theme/restyle/theme";
import { HStack, Text } from "../../primitive";
import { Pressable, PressableProps } from "../../primitive/Pressable/Pressable";
import { Badge } from "./Badge";
import { ButtonSpinner } from "./ButtonSpinner";
import {
  getBaseStyles,
  getTextStyles,
  applyCustomColorStyles,
  RestyleProps,
  getHoverStyles,
  getPressedStyles,
} from "./buttonStyles";
import { assignIconStyles } from "./iconStyles";

export const buttonVariants = [
  "main",
  "secondary",
  "white",
  "danger",
  "outline",
  "ghost",
] as const;

export const buttonSizes = ["small", "regular", "tiny", "large"] as const;

// Button variant types
export type ButtonVariant = (typeof buttonVariants)[number];

// Button size types
export type ButtonSize = (typeof buttonSizes)[number];

// Button props interface
export interface ButtonProps extends Omit<PressableProps, keyof RestyleProps> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  label?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  isLoading?: boolean;
  isDisabled?: boolean;
  badgeNumber?: number;
  color?: keyof Theme["colors"] | "transparent";
  testID?: string;
  "data-testid"?: string;
  accessibilityLabel: string;
  justifyContent?: RestyleProps["justifyContent"];
  width?: PressableProps["width"] | number;
}

// Compose the restyle functions
const restyleFunctions = composeRestyleFunctions<Theme, RestyleProps>([
  spacing,
  color,
  border,
  layout,
  backgroundColor,
  opacity,
  position,
  shadow,
]);

export const Button: React.FC<ButtonProps> = ({
  variant = "main",
  size = "regular",
  label,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  isLoading = false,
  isDisabled = false,
  badgeNumber,
  color,
  testID,
  "data-testid": dataTestId,
  accessibilityLabel,
  justifyContent = "center",
  width,
  role = "button",
  children,
  ...restProps
}) => {
  const isDisabledState = !!isDisabled || !!restProps.disabled;

  const labelOrChildren = label || children;
  // Get the base styles and apply custom color modifications
  const baseStyles = getBaseStyles(variant, size);
  const modifiedBaseStyles = applyCustomColorStyles(
    baseStyles,
    variant,
    isDisabledState,
    color
  );

  // Get the text styles
  const textStyles = getTextStyles(variant, size, color);

  // Handle disabled state by modifying base styles
  if (isDisabledState) {
    modifiedBaseStyles.backgroundColor = "button-disabled-surface-neutral";
    textStyles.color = "button-disabled-text";
  }

  const restyleProps = useRestyle(restyleFunctions, {
    ...modifiedBaseStyles,
    ...restProps,
  });

  const hoverRestyleProps = useRestyle(restyleFunctions, {
    ...modifiedBaseStyles,
    ...restProps,
    ...getHoverStyles(variant, isDisabledState),
  });
  const pressedRestyleProps = useRestyle(restyleFunctions, {
    ...modifiedBaseStyles,
    ...restProps,
    ...getPressedStyles(variant, isDisabledState),
  });

  const { leftIcon, rightIcon } = assignIconStyles(
    LeftIcon,
    RightIcon,
    variant,
    isDisabledState
  );

  return (
    <Pressable
      {...restyleProps}
      disabled={isLoading || isDisabledState}
      aria-disabled={isLoading || isDisabledState}
      width={width ? width : labelOrChildren ? "auto" : undefined}
      role={role}
      testID={testID ?? dataTestId}
      aria-label={accessibilityLabel}
      _hover={hoverRestyleProps}
      _pressed={pressedRestyleProps}
    >
      {isLoading ? (
        <ButtonSpinner variant={variant} />
      ) : (
        <HStack
          gap={modifiedBaseStyles.gap}
          alignItems="center"
          height="100%"
          justifyContent={justifyContent}
          alignSelf="stretch"
        >
          {leftIcon}
          {labelOrChildren ? (
            <Text variant="mainBaseSemiBold" {...textStyles}>
              {labelOrChildren}
            </Text>
          ) : null}
          {rightIcon}
        </HStack>
      )}
      {badgeNumber !== undefined ? <Badge number={badgeNumber} /> : null}
    </Pressable>
  );
};
